<!-- ══ LOADING ══ -->
@if (!project()) {
  <div class="pdv-overlay flex items-center justify-center">
    <div class="flex flex-col items-center gap-3 text-white">
      <i class="fa-solid fa-spinner animate-spin text-3xl"></i>
      <p class="text-sm font-medium tracking-wide">Loading project…</p>
    </div>
  </div>
}

@if (project()) {

  <!-- ══ LIGHTBOX ══ -->
  @if (lightboxSrc()) {
    <div class="pdv-lightbox" (click)="closeLightbox()" role="dialog" aria-modal="true">
      <button class="pdv-lb-close" (click)="closeLightbox()" aria-label="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
      <img [src]="lightboxSrc()!" alt="Project photo" (click)="$event.stopPropagation()" />
    </div>
  }

  <!-- ══ DELETE CONFIRM ══ -->
  @if (showDeleteConfirm()) {
    <div class="pdv-overlay z-[70] flex items-center justify-center p-4"
         role="alertdialog" aria-modal="true" aria-labelledby="del-title">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-6 text-center">
        <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i class="fa-solid fa-trash-can text-red-600"></i>
        </div>
        <h3 id="del-title" class="text-base font-bold text-slate-900 mb-1">Delete Project?</h3>
        <p class="text-sm text-slate-500 mb-5">
          "<strong>{{ project()!.name }}</strong>" will be permanently removed.
        </p>
        <div class="flex gap-2">
          <button (click)="cancelDelete()" [disabled]="isDeleting()"
                  class="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition disabled:opacity-50">
            Cancel
          </button>
          <button (click)="deleteProject()" [disabled]="isDeleting()"
                  class="flex-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
            @if (isDeleting()) { <i class="fa-solid fa-spinner animate-spin"></i> } @else { <i class="fa-solid fa-trash-can"></i> }
            Delete
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ══ MAIN PANEL ══ -->
  <div class="pdv-overlay flex items-start justify-center overflow-y-auto py-4 px-3">
    <div class="pdv-panel w-full max-w-5xl">

      <div class="pdv-mobile-top">
        <button type="button" class="pdv-mobile-back" (click)="close()" aria-label="Go back">
          <i class="fa-solid fa-arrow-left"></i>
          <span>Back</span>
        </button>
      </div>

      <!-- ════════════ HEADER ════════════ -->
      <div class="pdv-header">

        <!-- LEFT: title + meta chips -->
        <div class="pdv-header-left">
          <h1 class="pdv-title">{{ project()!.name }}</h1>

          <div class="pdv-chips">
            @if (project()!.block) {
              <span class="pdv-chip">
                <i class="fa-solid fa-location-dot"></i>
                <span class="pdv-chip-label">Block</span>
                {{ project()!.block }}
              </span>
            }
            @if (gpDisplay()) {
              <span class="pdv-chip">
                <i class="fa-solid fa-landmark"></i>
                <span class="pdv-chip-label">GP</span>
                {{ gpDisplay() }}
              </span>
            }
            @if (project()!.scheme) {
              <span class="pdv-chip">
                <i class="fa-solid fa-layer-group"></i>
                <span class="pdv-chip-label">Scheme</span>
                {{ project()!.scheme }}
              </span>
            }
            @if (projectYear()) {
              <span class="pdv-chip">
                <i class="fa-solid fa-calendar-days"></i>
                <span class="pdv-chip-label">Year</span>
                {{ projectYear() }}
              </span>
            }
            @if (project()!.dept) {
              <span class="pdv-chip pdv-chip-dept">
                <i class="fa-solid fa-building-columns"></i>
                {{ project()!.dept }}
              </span>
            }
          </div>
        </div>

        <!-- RIGHT: dates + actions -->
        <div class="pdv-header-right">
          <div class="pdv-date-pills">
            <div class="pdv-date-pill">
              <span class="pdv-date-label">Updated</span>
              <span class="pdv-date-val">{{ formatTs(project()!.updatedAt) }}</span>
            </div>
            <div class="pdv-date-pill">
              <span class="pdv-date-label">Created</span>
              <span class="pdv-date-val">{{ formatTs(project()!.createdAt) }}</span>
            </div>
          </div>
          <div class="pdv-actions">
            @if (canEdit() && !isEditMode()) {
              <button (click)="openEditForm()" class="pdv-btn pdv-btn-edit">
                <i class="fa-solid fa-pen-to-square"></i><span>Edit</span>
              </button>
              @if (isFirestoreDoc()) {
                <button (click)="confirmDelete()" class="pdv-btn pdv-btn-del">
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              }
            }
            <button (click)="close()" class="pdv-btn pdv-btn-close" aria-label="Close">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- ════════════ EDIT FORM ════════════ -->
      @if (isEditMode()) {
        <div class="pdv-body">
          <form [formGroup]="editForm" (ngSubmit)="saveEdit()" novalidate>

            <!-- ── Core Fields ──────────────────────────────────────────── -->
            <div class="pdv-form-section">
              <h3 class="pdv-form-section-title">
                <i class="fa-solid fa-pen-to-square text-indigo-500"></i> Project Details
              </h3>
              <div class="pdv-form-grid">

                <!-- Project Name (full width) -->
                <div class="pdv-field col-span-full">
                  <label class="pdv-label" for="f-name">Project Name <span class="text-red-500">*</span></label>
                  <input id="f-name" type="text" formControlName="name" class="pdv-input"
                         [class.border-red-400]="editForm.get('name')?.invalid && editForm.get('name')?.touched"
                         placeholder="Enter project name" />
                  @if (editForm.get('name')?.invalid && editForm.get('name')?.touched) {
                    <p class="pdv-err">Name is required (min 3 characters).</p>
                  }
                </div>

                <!-- Scheme -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-scheme">Scheme</label>
                  <select id="f-scheme" formControlName="scheme" class="pdv-input">
                    <option value="">-- Select Scheme --</option>
                    @for (s of osdSchemeOptions(); track s) { <option [value]="s">{{ s }}</option> }
                  </select>
                </div>

                <!-- Block -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-block">Block <span class="text-red-500">*</span></label>
                  <select id="f-block" formControlName="block" class="pdv-input"
                          (change)="onBlockChange($any($event.target).value)">
                    <option value="">-- Select Block --</option>
                    @for (b of locationBlocks; track b) { <option [value]="b">{{ b }}</option> }
                  </select>
                </div>

                <!-- GP -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-gp">Gram Panchayat</label>
                  @if (isNacBlock()) {
                    <input id="f-gp" type="text" value="NAC" class="pdv-input" readonly />
                  } @else {
                    <select id="f-gp" formControlName="gp" class="pdv-input">
                      <option value="">-- Select GP --</option>
                      @for (gp of gpOptions(); track gp) { <option [value]="gp">{{ gp }}</option> }
                    </select>
                  }
                </div>

                <!-- Status -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-status">Status <span class="text-red-500">*</span></label>
                  <select id="f-status" formControlName="status" class="pdv-input">
                    @for (s of statusOptions; track s) { <option [value]="s">{{ s }}</option> }
                  </select>
                </div>

                <!-- Total Cost -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-cost">Total Cost (₹ Lakh) <span class="text-red-500">*</span></label>
                  <input id="f-cost" type="number" formControlName="cost" min="0" step="0.01" class="pdv-input" placeholder="0.00" />
                </div>

                <!-- Amount Spent -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-spent">Amount Spent (₹ Lakh) <span class="text-red-500">*</span></label>
                  <input id="f-spent" type="number" formControlName="spent" min="0" step="0.01" class="pdv-input" placeholder="0.00" />
                </div>

                <!-- Year of Creation -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-year">Year of Creation</label>
                  <select id="f-year" formControlName="yearOfCreation" class="pdv-input">
                    <option value="">-- Select Year --</option>
                    @for (y of yearOptions(); track y) { <option [value]="y">{{ y }}</option> }
                  </select>
                </div>

                <!-- Road Length -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-road">Road Length (km)</label>
                  <input id="f-road" type="number" formControlName="roadLength" min="0" step="0.01" class="pdv-input" placeholder="e.g. 2.5" />
                </div>

                <!-- Executant Name -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-exec-name">Executant Name</label>
                  <input id="f-exec-name" type="text" formControlName="executantName" class="pdv-input" placeholder="Contractor / agency name" />
                </div>

                <!-- Executant Contact -->
                <div class="pdv-field">
                  <label class="pdv-label" for="f-exec-contact">Executant Contact</label>
                  <input id="f-exec-contact" type="tel" formControlName="executantContact" class="pdv-input" placeholder="Mobile number" />
                </div>

              </div>
            </div>

            <!-- ── Photo Sections ──────────────────────────────────────── -->
            <div class="pdv-form-section">
              <h3 class="pdv-form-section-title">
                <i class="fa-solid fa-images text-indigo-500"></i> Site Photos
              </h3>

              @if (canUploadPhotos() && isUploadingPhotos()) {
                <div class="pdv-upload-progress-wrap">
                  <div class="pdv-upload-bar" [style.width.%]="photoUploadProgress()"></div>
                  <span class="pdv-upload-pct">{{ photoUploadProgress() }}%</span>
                </div>
              }

              <div class="pdv-photos-edit-grid">

                <!-- Before Work -->
                <div class="pdv-photo-edit-box">
                  <div class="pdv-photo-box-label pdv-label-before">
                    <i class="fa-solid fa-hourglass-start"></i> Before Work
                  </div>
                  <div class="pdv-photo-date-field">
                    <label>Before Date</label>
                    <input type="date" formControlName="photoDateBefore">
                  </div>
                  <div class="pdv-photo-thumbs">
                    @for (src of photoBeforePreviews(); track src; let i = $index) {
                      <div class="pdv-photo-thumb-edit">
                        <img [src]="src" alt="Before photo" />
                        @if (canUploadPhotos()) {
                          <button type="button" class="pdv-thumb-remove" (click)="removePhoto('before', i)" aria-label="Remove">
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        }
                      </div>
                    }
                    @if (photoBeforePreviews().length === 0) {
                      <div class="pdv-photo-edit-empty">
                        <i class="fa-regular fa-image"></i>
                        <span>No photos yet</span>
                      </div>
                    }
                  </div>
                  @if (canUploadPhotos()) {
                    <label class="pdv-photo-add-btn">
                      <i class="fa-solid fa-plus"></i> Add Photo
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple class="sr-only"
                             (change)="onPhotoSelected('before', $event)" />
                    </label>
                  }
                </div>

                <!-- During Work -->
                <div class="pdv-photo-edit-box">
                  <div class="pdv-photo-box-label pdv-label-during">
                    <i class="fa-solid fa-person-digging"></i> During Work
                  </div>
                  <div class="pdv-photo-date-field">
                    <label>During Date</label>
                    <input type="date" formControlName="photoDateDuring">
                  </div>
                  <div class="pdv-photo-thumbs">
                    @for (src of photoDuringPreviews(); track src; let i = $index) {
                      <div class="pdv-photo-thumb-edit">
                        <img [src]="src" alt="During photo" />
                        @if (canUploadPhotos()) {
                          <button type="button" class="pdv-thumb-remove" (click)="removePhoto('during', i)" aria-label="Remove">
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        }
                      </div>
                    }
                    @if (photoDuringPreviews().length === 0) {
                      <div class="pdv-photo-edit-empty">
                        <i class="fa-regular fa-image"></i>
                        <span>No photos yet</span>
                      </div>
                    }
                  </div>
                  @if (canUploadPhotos()) {
                    <label class="pdv-photo-add-btn">
                      <i class="fa-solid fa-plus"></i> Add Photo
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple class="sr-only"
                             (change)="onPhotoSelected('during', $event)" />
                    </label>
                  }
                </div>

                <!-- After Work -->
                <div class="pdv-photo-edit-box">
                  <div class="pdv-photo-box-label pdv-label-after">
                    <i class="fa-solid fa-circle-check"></i> After Work
                  </div>
                  <div class="pdv-photo-date-field">
                    <label>After Date</label>
                    <input type="date" formControlName="photoDateAfter">
                  </div>
                  <div class="pdv-photo-thumbs">
                    @for (src of photoAfterPreviews(); track src; let i = $index) {
                      <div class="pdv-photo-thumb-edit">
                        <img [src]="src" alt="After photo" />
                        @if (canUploadPhotos()) {
                          <button type="button" class="pdv-thumb-remove" (click)="removePhoto('after', i)" aria-label="Remove">
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        }
                      </div>
                    }
                    @if (photoAfterPreviews().length === 0) {
                      <div class="pdv-photo-edit-empty">
                        <i class="fa-regular fa-image"></i>
                        <span>No photos yet</span>
                      </div>
                    }
                  </div>
                  @if (canUploadPhotos()) {
                    <label class="pdv-photo-add-btn">
                      <i class="fa-solid fa-plus"></i> Add Photo
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple class="sr-only"
                             (change)="onPhotoSelected('after', $event)" />
                    </label>
                  }
                </div>

              </div>
            </div>

            <!-- ── OSD Special Flags ──────────────────────────────────── -->
            <div class="pdv-form-section">
              <h3 class="pdv-form-section-title pdv-section-title-osd">
                <i class="fa-solid fa-shield-halved"></i> OSD Special Flags
              </h3>
              <div class="pdv-osd-checks">

                <!-- HM Decision Dashboard -->
                <label class="pdv-check-item">
                  <input type="checkbox" formControlName="hmPriority" class="pdv-check-input" />
                  <div class="pdv-check-content pdv-check-hm">
                    <i class="fa-solid fa-star pdv-check-icon"></i>
                    <div>
                      <p class="pdv-check-title">HM Decision Dashboard</p>
                      <p class="pdv-check-desc">Escalate this project to HM priority attention</p>
                    </div>
                  </div>
                </label>

                <!-- Recurring Intervention -->
                <label class="pdv-check-item">
                  <input type="checkbox" formControlName="recurringIntervention" class="pdv-check-input" />
                  <div class="pdv-check-content pdv-check-recurring">
                    <i class="fa-solid fa-rotate pdv-check-icon"></i>
                    <div>
                      <p class="pdv-check-title">Recurring Intervention</p>
                      <p class="pdv-check-desc">Requires ongoing follow-up and intervention</p>
                    </div>
                  </div>
                </label>

                <!-- Delayed Project -->
                <label class="pdv-check-item">
                  <input type="checkbox" formControlName="isDelayed" class="pdv-check-input" />
                  <div class="pdv-check-content pdv-check-delayed">
                    <i class="fa-solid fa-clock-rotate-left pdv-check-icon"></i>
                    <div>
                      <p class="pdv-check-title">Delayed Project</p>
                      <p class="pdv-check-desc">Project is behind schedule or significantly delayed</p>
                    </div>
                  </div>
                </label>

              </div>

              @if (editForm.get('isDelayed')?.value) {
                <div class="pdv-delayed-reason-wrap">
                  <label class="pdv-field-label">
                    <i class="fa-solid fa-comment-dots"></i> Delay Reason
                    <span class="pdv-field-required">*</span>
                  </label>
                  <textarea
                    formControlName="delayedReason"
                    class="pdv-textarea pdv-delayed-reason-input"
                    placeholder="Describe why this project is delayed (e.g. land acquisition issue, contractor dispute, funding gap)…"
                    rows="2"></textarea>
                </div>
              }

            </div>

            @if (saveError()) {
              <div class="px-6 pb-4">
                <p class="pdv-alert-err"><i class="fa-solid fa-triangle-exclamation mr-1"></i>{{ saveError() }}</p>
              </div>
            }
          </form>
        </div>

        <div class="pdv-footer">
          <button (click)="cancelEdit()" [disabled]="isSaving() || isUploadingPhotos()" class="pdv-btn pdv-btn-secondary">Cancel</button>
          <button (click)="saveEdit()" [disabled]="isSaving() || isUploadingPhotos()" class="pdv-btn pdv-btn-primary">
            @if (isSaving() || isUploadingPhotos()) { <i class="fa-solid fa-spinner animate-spin"></i> Saving… }
            @else { <i class="fa-solid fa-floppy-disk"></i> Save Changes }
          </button>
        </div>
      }

      <!-- ════════════ VIEW MODE ════════════ -->
      @if (!isEditMode()) {
        <div class="pdv-body">

          <!-- ── DETAIL CARDS (7 items) ──────────────────────────────────── -->
          <div class="pdv-cards-grid">

            <!-- 1. Total Cost -->
            <div class="pdv-card pdv-card-green">
              <div class="pdv-card-icon-wrap pdv-icon-green">
                <i class="fa-solid fa-indian-rupee-sign"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Total Cost</p>
                <p class="pdv-card-value">₹{{ project()!.cost | number:'1.0-2' }}<span class="pdv-card-unit">L</span></p>
              </div>
            </div>

            <!-- 2. Status -->
            <div class="pdv-card pdv-card-status">
              <div class="pdv-card-icon-wrap pdv-icon-slate">
                <i class="fa-solid fa-circle-dot"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Status</p>
                <span [class]="'pdv-status-badge ' + getStatusClass(project()!.status)">
                  {{ project()!.status }}
                </span>
              </div>
            </div>

            <!-- 3. Amount Spent -->
            <div class="pdv-card pdv-card-teal">
              <div class="pdv-card-icon-wrap pdv-icon-teal">
                <i class="fa-solid fa-money-bill-transfer"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Amount Spent</p>
                <p class="pdv-card-value">₹{{ project()!.spent | number:'1.0-2' }}<span class="pdv-card-unit">L</span></p>
              </div>
            </div>

            <!-- 4. Year of Creation -->
            <div class="pdv-card pdv-card-amber">
              <div class="pdv-card-icon-wrap pdv-icon-amber">
                <i class="fa-solid fa-calendar-check"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Year of Creation</p>
                <p class="pdv-card-value pdv-card-value-md">{{ projectYear() || '—' }}</p>
              </div>
            </div>

            <!-- 5. Road Length -->
            <div class="pdv-card pdv-card-purple">
              <div class="pdv-card-icon-wrap pdv-icon-purple">
                <i class="fa-solid fa-road"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Road Length</p>
                @if (project()!.roadLength) {
                  <p class="pdv-card-value">{{ project()!.roadLength }}<span class="pdv-card-unit">km</span></p>
                } @else {
                  <p class="pdv-card-na">Not available</p>
                }
              </div>
            </div>

            <!-- 6. Executant Name -->
            <div class="pdv-card pdv-card-blue">
              <div class="pdv-card-icon-wrap pdv-icon-blue">
                <i class="fa-solid fa-user-hard-hat"></i>
              </div>
              <div class="pdv-card-body min-w-0">
                <p class="pdv-card-label">Executant Name</p>
                @if (executantName()) {
                  <p class="pdv-card-value pdv-card-value-md truncate">{{ executantName() }}</p>
                } @else {
                  <p class="pdv-card-na">Not Assigned</p>
                }
              </div>
            </div>

            <!-- 7. Executant Contact -->
            <div class="pdv-card pdv-card-indigo">
              <div class="pdv-card-icon-wrap pdv-icon-indigo">
                <i class="fa-solid fa-phone"></i>
              </div>
              <div class="pdv-card-body">
                <p class="pdv-card-label">Executant Contact</p>
                @if (executantContact()) {
                  <a [href]="'tel:' + executantContact()" class="pdv-card-link">
                    {{ executantContact() }}
                  </a>
                } @else {
                  <p class="pdv-card-na">Not Assigned</p>
                }
              </div>
            </div>

          </div>

          <!-- ── OSD INDICATORS (OSD role only) ───────────────────────── -->
          @if (isOsd() && (project()!.hmPriority || project()!.recurringIntervention || project()!.isDelayed)) {
            <div class="pdv-osd-indicators">
              @if (project()!.hmPriority) {
                <span class="pdv-osd-badge pdv-osd-hm">
                  <i class="fa-solid fa-star"></i>
                  Marked for HM Decision
                </span>
              }
              @if (project()!.recurringIntervention) {
                <span class="pdv-osd-badge pdv-osd-recurring">
                  <i class="fa-solid fa-rotate"></i>
                  Recurring Intervention Enabled
                </span>
              }
              @if (project()!.isDelayed) {
                <span class="pdv-osd-badge pdv-osd-delayed">
                  <i class="fa-solid fa-clock-rotate-left"></i>
                  Delayed Project
                </span>
              }
            </div>
          }

          <!-- ── PHOTOS ──────────────────────────────────────────────────── -->
          <div class="pdv-photos-section">
            <h2 class="pdv-section-title">
              <i class="fa-solid fa-images text-indigo-500"></i> Site Photos
            </h2>

            <div class="pdv-photos-grid">

              <!-- Before Work -->
              <div class="pdv-photo-box">
                <div class="pdv-photo-box-label pdv-label-before">
                  <i class="fa-solid fa-hourglass-start"></i>
                  Before Work
                  @if (countBefore() > 0) { <span class="pdv-photo-badge">{{ countBefore() }}</span> }
                </div>
                @if (photoDateBefore()) {
                  <div class="pdv-photo-date"><i class="fa-regular fa-calendar"></i> {{ photoDateBefore() | date:'mediumDate' }}</div>
                }

                @if (firstBefore()) {
                  <div class="pdv-photo-frame" (click)="openLightbox(firstBefore()!)"
                       role="button" tabindex="0" (keydown.enter)="openLightbox(firstBefore()!)" aria-label="View before photo">
                    <img [src]="firstBefore()!" alt="Before work photo" loading="lazy" />
                    <div class="pdv-photo-hover">
                      <i class="fa-solid fa-expand text-white text-xl"></i>
                    </div>
                    @if (countBefore() > 1) {
                      <div class="pdv-photo-more">+{{ countBefore() - 1 }} more</div>
                    }
                  </div>
                } @else {
                  <div class="pdv-photo-placeholder">
                    <i class="fa-regular fa-image text-3xl text-slate-300"></i>
                    <p>No photo uploaded</p>
                  </div>
                }

                <!-- Extra thumbnails for before -->
                @if (photoBefore().length > 1) {
                  <div class="pdv-thumb-strip">
                    @for (src of photoBefore().slice(1); track src; let i = $index) {
                      @if (i < 4) {
                        <div class="pdv-thumb" (click)="openLightbox(src)" role="button" tabindex="0"
                             (keydown.enter)="openLightbox(src)">
                          <img [src]="src" alt="Photo" loading="lazy" />
                        </div>
                      }
                    }
                  </div>
                }
                @if (photosLegacy().length > 1) {
                  <div class="pdv-thumb-strip">
                    @for (src of photosLegacy().slice(1); track src; let i = $index) {
                      @if (i < 4) {
                        <div class="pdv-thumb" (click)="openLightbox(src)" role="button" tabindex="0"
                             (keydown.enter)="openLightbox(src)">
                          <img [src]="src" alt="Photo" loading="lazy" />
                        </div>
                      }
                    }
                  </div>
                }
              </div>

              <!-- During Work -->
              <div class="pdv-photo-box">
                <div class="pdv-photo-box-label pdv-label-during">
                  <i class="fa-solid fa-person-digging"></i>
                  During Work
                  @if (countDuring() > 0) { <span class="pdv-photo-badge">{{ countDuring() }}</span> }
                </div>
                @if (photoDateDuring()) {
                  <div class="pdv-photo-date"><i class="fa-regular fa-calendar"></i> {{ photoDateDuring() | date:'mediumDate' }}</div>
                }

                @if (firstDuring()) {
                  <div class="pdv-photo-frame" (click)="openLightbox(firstDuring()!)"
                       role="button" tabindex="0" (keydown.enter)="openLightbox(firstDuring()!)" aria-label="View during photo">
                    <img [src]="firstDuring()!" alt="During work photo" loading="lazy" />
                    <div class="pdv-photo-hover">
                      <i class="fa-solid fa-expand text-white text-xl"></i>
                    </div>
                    @if (countDuring() > 1) {
                      <div class="pdv-photo-more">+{{ countDuring() - 1 }} more</div>
                    }
                  </div>
                } @else {
                  <div class="pdv-photo-placeholder">
                    <i class="fa-regular fa-image text-3xl text-slate-300"></i>
                    <p>No photo uploaded</p>
                  </div>
                }

                @if (photoDuring().length > 1) {
                  <div class="pdv-thumb-strip">
                    @for (src of photoDuring().slice(1); track src; let i = $index) {
                      @if (i < 4) {
                        <div class="pdv-thumb" (click)="openLightbox(src)" role="button" tabindex="0"
                             (keydown.enter)="openLightbox(src)">
                          <img [src]="src" alt="Photo" loading="lazy" />
                        </div>
                      }
                    }
                  </div>
                }
              </div>

              <!-- After Work -->
              <div class="pdv-photo-box">
                <div class="pdv-photo-box-label pdv-label-after">
                  <i class="fa-solid fa-circle-check"></i>
                  After Work
                  @if (countAfter() > 0) { <span class="pdv-photo-badge">{{ countAfter() }}</span> }
                </div>
                @if (photoDateAfter()) {
                  <div class="pdv-photo-date"><i class="fa-regular fa-calendar"></i> {{ photoDateAfter() | date:'mediumDate' }}</div>
                }

                @if (firstAfter()) {
                  <div class="pdv-photo-frame" (click)="openLightbox(firstAfter()!)"
                       role="button" tabindex="0" (keydown.enter)="openLightbox(firstAfter()!)" aria-label="View after photo">
                    <img [src]="firstAfter()!" alt="After work photo" loading="lazy" />
                    <div class="pdv-photo-hover">
                      <i class="fa-solid fa-expand text-white text-xl"></i>
                    </div>
                    @if (countAfter() > 1) {
                      <div class="pdv-photo-more">+{{ countAfter() - 1 }} more</div>
                    }
                  </div>
                } @else {
                  <div class="pdv-photo-placeholder">
                    <i class="fa-regular fa-image text-3xl text-slate-300"></i>
                    <p>No photo uploaded</p>
                  </div>
                }

                @if (photoAfter().length > 1) {
                  <div class="pdv-thumb-strip">
                    @for (src of photoAfter().slice(1); track src; let i = $index) {
                      @if (i < 4) {
                        <div class="pdv-thumb" (click)="openLightbox(src)" role="button" tabindex="0"
                             (keydown.enter)="openLightbox(src)">
                          <img [src]="src" alt="Photo" loading="lazy" />
                        </div>
                      }
                    }
                  </div>
                }
              </div>

            </div>

            <!-- Photo upload control (operator/admin only) -->
            @if (canUploadPhotos()) {
              <div class="pdv-photo-upload-wrap">
                @if (photoSaveError()) {
                  <p class="pdv-alert-err mb-2"><i class="fa-solid fa-triangle-exclamation mr-1"></i>{{ photoSaveError() }}</p>
                }
                <app-photo-upload
                  [photos]="project()!.photos ?? []"
                  [projectId]="project()!.id"
                  [canEdit]="true"
                  (photosChange)="onPhotosChanged($event)" />
              </div>
            }
          </div>

        </div>

        <!-- Footer -->
        <div class="pdv-footer">
          <button (click)="close()" class="pdv-btn pdv-btn-secondary">
            <i class="fa-solid fa-arrow-left"></i> Back
          </button>
        </div>
      }

    </div>
  </div>

  <!-- ══ SUCCESS TOAST ══ -->
  @if (saveSuccess()) {
    <div class="pdv-toast-success" role="status" aria-live="polite">
      <i class="fa-solid fa-circle-check"></i>
      Project updated successfully!
    </div>
  }

}
